import React from "react";
import classNames from "classnames";
import useWizardPage from "./useWizardPage";

/**
 * Default FinishPage Component - Automatically shown as the last page
 */
const FinishPage = () => {
    const { inputManifest, emittedManifest, setEmittedManifest } =
        useWizardPage();
    const safeManifest = emittedManifest || {};

    const [showRawJson, setShowRawJson] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [navigationPath, setNavigationPath] = React.useState([]);
    const [editedJson, setEditedJson] = React.useState(
        JSON.stringify(safeManifest, null, 2)
    );
    const [jsonError, setJsonError] = React.useState(null);

    // Update editedJson when emittedManifest changes
    React.useEffect(() => {
        if (!isEditing) {
            setEditedJson(JSON.stringify(safeManifest, null, 2));
        }
    }, [safeManifest, isEditing]);

    const handleJsonChange = (e) => {
        const newJson = e.target.value;
        setEditedJson(newJson);
        try {
            JSON.parse(newJson);
            setJsonError(null);
        } catch (err) {
            setJsonError(err.message);
        }
    };

    const handleSaveJson = () => {
        try {
            const parsed = JSON.parse(editedJson);
            setEmittedManifest(parsed);
            setIsEditing(false);
            setJsonError(null);
        } catch (err) {
            setJsonError(err.message);
        }
    };

    const handleCopyJson = () => {
        navigator.clipboard.writeText(
            editedJson || JSON.stringify(safeManifest, null, 2)
        );
    };

    const navigateTo = (key) => {
        setNavigationPath([...navigationPath, key]);
    };

    const navigateToLevel = (level) => {
        setNavigationPath(level === -1 ? [] : navigationPath.slice(0, level));
    };

    const getIcon = (value) => {
        if (Array.isArray(value)) return "fa-list";
        if (typeof value === "object" && value !== null) return "fa-ellipsis-h";
        return "fa-circle";
    };

    const renderPaneNavigator = () => {
        const currentObj = navigationPath.reduce(
            (obj, key) => obj[key],
            safeManifest
        );

        const currentTitle =
            navigationPath.length === 0
                ? "Manifest"
                : navigationPath[navigationPath.length - 1];

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "250px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    overflow: "hidden",
                    backgroundColor: "#fafafa",
                }}
            >
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div style={{ padding: "8px" }}>
                        <div
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#666",
                                padding: "8px",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #eee",
                                marginBottom: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>{currentTitle}</span>
                            {navigationPath.length > 0 && (
                                <button
                                    className="btn btn-link p-0"
                                    onClick={() =>
                                        navigateToLevel(
                                            navigationPath.length - 1
                                        )
                                    }
                                    style={{
                                        fontSize: "12px",
                                        color: "#007bff",
                                        textDecoration: "none",
                                    }}
                                    title="Go up one level"
                                >
                                    <i className="fa fa-chevron-left mr-1"></i>
                                    Up
                                </button>
                            )}
                        </div>
                        {navigationPath.length > 0 && (
                            <div
                                onClick={() =>
                                    navigateToLevel(navigationPath.length - 1)
                                }
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #eee",
                                    fontSize: "13px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    backgroundColor: "#f8f9fa",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "#e8e8e8";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "#f8f9fa";
                                }}
                            >
                                <i
                                    className="fa fa-level-up-alt"
                                    style={{
                                        fontSize: "11px",
                                        color: "#007bff",
                                        width: "12px",
                                    }}
                                />
                                <span style={{ fontWeight: "600" }}>..</span>
                            </div>
                        )}
                        {typeof currentObj === "object" &&
                        currentObj !== null ? (
                            Object.entries(currentObj).map(([key, value]) => (
                                <div
                                    key={key}
                                    onClick={() => {
                                        if (
                                            typeof value === "object" &&
                                            value !== null
                                        ) {
                                            navigateTo(key);
                                        }
                                    }}
                                    style={{
                                        padding: "8px 12px",
                                        cursor:
                                            typeof value === "object" &&
                                            value !== null
                                                ? "pointer"
                                                : "default",
                                        borderBottom: "1px solid #eee",
                                        fontSize: "13px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (
                                            typeof value === "object" &&
                                            value !== null
                                        ) {
                                            e.currentTarget.style.backgroundColor =
                                                "#e8e8e8";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            "transparent";
                                    }}
                                >
                                    <i
                                        className={`fa ${getIcon(value)}`}
                                        style={{
                                            fontSize: "11px",
                                            color: "#999",
                                            width: "12px",
                                        }}
                                    />
                                    <span
                                        style={{
                                            flex: 1,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {key}
                                    </span>
                                    {typeof value !== "object" ||
                                    value === null ? (
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                color: "#999",
                                                marginLeft: "auto",
                                            }}
                                        >
                                            {String(value).slice(0, 20)}
                                        </span>
                                    ) : (
                                        <i
                                            className="fa fa-chevron-right"
                                            style={{
                                                fontSize: "10px",
                                                color: "#ccc",
                                            }}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div
                                style={{
                                    padding: "12px",
                                    color: "#666",
                                    fontSize: "13px",
                                    wordBreak: "break-all",
                                }}
                            >
                                {JSON.stringify(currentObj, null, 2)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="alert alert-success">
                <h4 className="alert-heading">
                    <i className="fa fa-check-circle mr-2"></i>
                    Ready to Complete!
                </h4>
                <p className="mb-3">
                    Review your configuration below and click Finish when ready.
                </p>
            </div>

            <div className="card mb-3">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Configuration Summary</h5>
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            className={classNames("btn", {
                                "btn-primary": !showRawJson,
                                "btn-outline-primary": showRawJson,
                            })}
                            onClick={() => setShowRawJson(false)}
                        >
                            <i className="fa fa-folder-open mr-1"></i> Navigator
                        </button>
                        <button
                            type="button"
                            className={classNames("btn", {
                                "btn-primary": showRawJson,
                                "btn-outline-primary": !showRawJson,
                            })}
                            onClick={() => setShowRawJson(true)}
                        >
                            <i className="fa fa-code mr-1"></i> Raw
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {showRawJson ? (
                        <div>
                            <div className="d-flex gap-2 mb-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setIsEditing(!isEditing)}
                                >
                                    <i
                                        className={classNames("fa", {
                                            "fa-edit": !isEditing,
                                            "fa-times": isEditing,
                                        })}
                                    />
                                    {isEditing ? " Cancel Edit" : " Edit JSON"}
                                </button>
                                {isEditing && (
                                    <button
                                        className="btn btn-sm btn-success"
                                        onClick={handleSaveJson}
                                        disabled={!!jsonError}
                                    >
                                        <i className="fa fa-save mr-1"></i> Save
                                    </button>
                                )}
                                <button
                                    className="btn btn-sm btn-outline-secondary ms-auto"
                                    onClick={handleCopyJson}
                                >
                                    <i className="fa fa-copy mr-1"></i> Copy
                                </button>
                            </div>
                            {jsonError && (
                                <div className="alert alert-danger mb-2">
                                    <small>{jsonError}</small>
                                </div>
                            )}
                            <div
                                style={{
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                }}
                            >
                                {isEditing ? (
                                    <textarea
                                        className="form-control"
                                        style={{
                                            fontFamily: "monospace",
                                            fontSize: "12px",
                                            height: "400px",
                                            border: "none",
                                            borderRadius: 0,
                                            backgroundColor: jsonError
                                                ? "#ffe6e6"
                                                : "white",
                                        }}
                                        value={editedJson}
                                        onChange={handleJsonChange}
                                    />
                                ) : (
                                    <pre
                                        style={{
                                            padding: "12px",
                                            margin: 0,
                                            fontSize: "12px",
                                            maxHeight: "400px",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {JSON.stringify(safeManifest, null, 2)}
                                    </pre>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {/* Breadcrumb Navigation - Always visible */}
                            <div className="mb-3 pb-2 border-bottom">
                                <nav style={{ fontSize: "13px" }}>
                                    <button
                                        className="btn btn-link p-0 text-decoration-none"
                                        onClick={() => navigateToLevel(-1)}
                                        style={{
                                            color:
                                                navigationPath.length === 0
                                                    ? "#666"
                                                    : "#007bff",
                                            cursor:
                                                navigationPath.length === 0
                                                    ? "default"
                                                    : "pointer",
                                            fontWeight:
                                                navigationPath.length === 0
                                                    ? "600"
                                                    : "normal",
                                        }}
                                        disabled={navigationPath.length === 0}
                                    >
                                        <i className="fa fa-home mr-1"></i>
                                        Manifest
                                    </button>
                                    {navigationPath.map((pathKey, index) => (
                                        <span key={index}>
                                            <span className="mx-2">/</span>
                                            <button
                                                className="btn btn-link p-0 text-decoration-none"
                                                onClick={() =>
                                                    navigateToLevel(index)
                                                }
                                                style={{
                                                    color:
                                                        index ===
                                                        navigationPath.length -
                                                            1
                                                            ? "#666"
                                                            : "#007bff",
                                                    cursor: "pointer",
                                                    fontWeight:
                                                        index ===
                                                        navigationPath.length -
                                                            1
                                                            ? "600"
                                                            : "normal",
                                                }}
                                            >
                                                {pathKey}
                                            </button>
                                        </span>
                                    ))}
                                </nav>
                            </div>
                            {renderPaneNavigator()}
                        </div>
                    )}
                </div>
            </div>

            <div className="alert alert-info mb-0">
                <p className="mb-0">
                    <b>Configuration is ready!</b> Click <strong>Finish</strong>{" "}
                    to complete.
                </p>
            </div>
        </div>
    );
};

export default FinishPage;
